import { type AcknowledgmentResponse, acknowledgmentResponseSchema } from '@sivan/shared';
import { useId, useState } from 'react';
import { ApiError, NetworkError, apiPost } from '../api/client';
import { askConfirm } from '../ui/confirm';

/**
 * The organization's acknowledgment wording, entered by a business Super Admin from the approved
 * source. The application has no wording of its own: until one is entered, handovers cannot be
 * closed. A change is a new version; closed protocols keep the version they were signed under.
 */
export function AcknowledgmentEditor({ initial }: { readonly initial: AcknowledgmentResponse }) {
  const id = useId();
  // The saved version is kept here (no reload), so the confirmation stays on screen.
  const [current, setCurrent] = useState(initial.current);
  const [text, setText] = useState(current?.text ?? '');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const changed = text.trim() !== (current?.text ?? '');
  const ready = changed && text.trim().length >= 20 && source.trim().length >= 3;
  const save = () => {
    void askConfirm({
      title: current === null ? 'לשמור את נוסח ההצהרה?' : 'לשמור גרסה חדשה של הנוסח?',
      message:
        'הנוסח יוצג לחותמים ויופיע בכל פרוטוקול שייסגר מעכשיו. פרוטוקולים שכבר נסגרו נשארים עם הנוסח שלהם. ודאו שזה הנוסח המאושר.',
      confirmLabel: 'שמירת הנוסח',
    }).then((yes) => {
      if (!yes) return;
      setBusy(true);
      setMessage(null);
      apiPost(
        '/handover-acknowledgment',
        { text: text.trim(), source: source.trim() },
        acknowledgmentResponseSchema,
      )
        .then((next) => {
          setSource('');
          setCurrent(next.current);
          setMessage({
            kind: 'success',
            text: `נשמרה גרסה ${String(next.current?.version ?? '')}.`,
          });
        })
        .catch((e: unknown) => {
          setMessage({
            kind: 'error',
            text:
              e instanceof NetworkError
                ? 'אין חיבור לשרת. הנוסח לא נשמר.'
                : e instanceof ApiError && e.status === 403
                  ? 'רק מנהל־על עסקי (עם אימות דו־שלבי) רשאי לשנות את הנוסח.'
                  : 'שמירת הנוסח נכשלה.',
          });
        })
        .finally(() => {
          setBusy(false);
        });
    });
  };
  return (
    <section className="acknowledgment-editor" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`}>נוסח ההצהרה בפרוטוקול המסירה</h3>
      <p className="hint">
        הנוסח שהמקבל חותם עליו, לכל הפרויקטים של הארגון. המערכת אינה מספקת נוסח משלה: יש להזין את
        הנוסח המאושר בדיוק כפי שאושר. עד שיוזן, אי אפשר לסגור מסירות.
      </p>
      {current === null ? (
        <p className="notice notice--warning">עדיין לא הוזן נוסח.</p>
      ) : (
        <p className="hint">
          גרסה נוכחית: {current.version} · מקור: {current.source}
        </p>
      )}
      <label className="field" htmlFor={`${id}-text`}>
        <span>נוסח ההצהרה</span>
        <textarea
          id={`${id}-text`}
          rows={8}
          maxLength={4000}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
      </label>
      <label className="field" htmlFor={`${id}-source`}>
        <span>מקור האישור (למשל: שם המסמך המאושר ותאריכו)</span>
        <input
          id={`${id}-source`}
          maxLength={300}
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
          }}
        />
      </label>
      {message !== null && (
        <p
          className={`notice notice--${message.kind}`}
          role={message.kind === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      )}
      <div className="form-actions">
        <button
          type="button"
          className="button button--primary"
          disabled={!ready || busy}
          onClick={save}
        >
          {current === null ? 'שמירת הנוסח' : 'שמירת גרסה חדשה'}
        </button>
      </div>
    </section>
  );
}
